// @ts-strict-ignore
import React, { useState } from 'react'

import dayjs from 'dayjs'
import { get } from 'lodash'

import { getSlotFromTime } from '~/utils/graphqlDataFormatters'
import { useFeatureIsOn } from '~/utils/growthbook'

import ListingPage from '~/components/Listing/ListingPageContent'
import { useListingQuery } from '~/components/Listing/ListingPageContent/ListingPageContentUtils'
import { DailyCardView } from '~/components/Restaurant/Card'
import {
  ReservableEnum,
  useAllTagsQuery,
  useClientConfigQuery,
  useOngoingFestivalsListQuery,
  useRestaurantsListQuery,
} from '~/generated/graphql'
import { NextPageWithLayout } from '~/pages/_app'
import { Dialog } from '~/shared/molecules/Dialog'
import { useLocation } from '~/shared/molecules/LocationMenu/LocationMenuUtils'
import { D2DMapModal, MapButton } from '~/shared/molecules/Map/MapModalUtils'

import { defaultFilterSettings, getListingBanners, getRestaurantListQueryVariables, PAGE_SIZE } from './RestaurantListingPageUtils'

interface Props {
  availableDatesISOString: string[]
  minDateISOString?: string
}

const RestaurantListingPage: NextPageWithLayout<Props> = ({ availableDatesISOString, minDateISOString }) => {
  const { loading, fetchMore, setFilters, filters, isFetchingMore, data } = useListingQuery({
    getQueryVariables: getRestaurantListQueryVariables,
    useQueryHook: useRestaurantsListQuery,
    pageSize: PAGE_SIZE,
  })

  const isD2DListingMapEnabled = useFeatureIsOn('d2d-listing-map')

  const [mapDialogOpen, setMapDialogOpen] = useState(false)

  const { data: tagsData } = useAllTagsQuery({ fetchPolicy: 'cache-first', variables: { regionId: filters.selectedRegion.id } })
  const tags = tagsData?.tags || []
  const { data: clientConfig } = useClientConfigQuery()
  const reservableFrom = dayjs.parseZone(clientConfig?.clientConfigs.d2dDefaultReservableFrom)
  const { data: ongoingFestivalsData } = useOngoingFestivalsListQuery()
  const festivalListingBanners = getListingBanners(ongoingFestivalsData?.ongoingFestivalEditions)

  const items = data?.restaurants.nodes

  // const otherDatesRestaurantsData = useRestaurantsInOtherDates({ filters })

  const { regions } = useLocation()

  const handleOpenMapDialog = () => {
    setMapDialogOpen(true)
  }

  const mapButton = isD2DListingMapEnabled && items?.length > 0 && <MapButton onClick={handleOpenMapDialog} />

  return (
    <>
      <ListingPage
        availableDatesISOString={availableDatesISOString}
        minDateISOString={minDateISOString}
        defaultSlot={getSlotFromTime(reservableFrom)}
        defaultDate={reservableFrom}
        defaultFilterSettings={defaultFilterSettings}
        filterSections={['chefsmenu', 'cuisines', 'dishes']}
        aboveFiltersElement={mapButton}
        festivalListingBanners={festivalListingBanners}
        items={items || []}
        fetchMore={() => fetchMore({ variables: { after: items.length.toString() } })}
        renderItem={(item, currentSearch) => (
          <DailyCardView
            key={item.id}
            type='daily'
            subtitle={item.description}
            restaurant={item}
            imageFiles={item.imageFiles}
            slotParams={currentSearch}
            withCarousel
            reservableInput={{ id: get(item, 'reservables[0].id'), type: ReservableEnum.Daily }}
          />
        )}
        totalCount={data?.restaurants.totalCount}
        isLoading={(!data && loading) || !tags}
        isFetchingMore={isFetchingMore}
        regions={regions}
        onFiltersChange={setFilters}
        withSearch
        allTags={tags}
        type='daily'
        showExtraGuest
        // commented pending improvement of query and backend otherDatesRestaurantsData
        // renderListEndContent={() =>
        //   hasRestaurantsAvailableInOtherDates(otherDatesRestaurantsData?.otherDatesRestaurants) ? (
        //     <>
        //       <OtherDatesRestaurantsTitle>{gt.tp('ListingPage', 'Check restaurants available at other dates!')}</OtherDatesRestaurantsTitle>
        //       {otherDatesRestaurantsData?.otherDatesRestaurants.nodes.map(item => (
        //         <DailyCardView
        //           key={item.id}
        //           type='daily'
        //           subtitle={item.description}
        //           restaurant={item}
        //           imageFiles={item.imageFiles}
        //           slotParams={{ peopleCount: filters.search.peopleCount }}
        //           withCarousel
        //           reservableInput={{ id: get(item, 'reservables[0].id'), type: ReservableEnum.Daily }}
        //         />
        //       ))}
        //     </>
        //   ) : null
        // }
      />
      <Dialog
        dialogContext={{
          open: mapDialogOpen,
          onOpenChange: () => setMapDialogOpen(false),
          'open.controlled': true,
          id: 'mapDialog',
        }}>
        <D2DMapModal restaurantNodes={items} loading={loading} isFetchingMore={isFetchingMore} />
      </Dialog>
    </>
  )
}

export default RestaurantListingPage
